import {encodeGlobalId} from '../../../../forecast-app/shared/util/GlobalIdUtil';

/** The company id Forecast's own staff belong to; their person records carry no company field. */
const FORECAST_COMPANY_ID = 1;

export interface DirectoryCompanySource {
	readonly id: string | null;
	readonly companyId: number | null;
	readonly name: string | null;
	readonly accessBlocked?: boolean | null;
}

export interface DirectoryPersonSource {
	readonly id: string | null;
	readonly firstName: string | null;
	readonly lastName: string | null;
	readonly email: string | null;
	readonly companyId?: string | null;
}

export interface DeployDirectorySource {
	readonly companies: ReadonlyArray<DirectoryCompanySource | null> | null;
	readonly forecastPersons: ReadonlyArray<DirectoryPersonSource | null> | null;
	readonly otherPersons: ReadonlyArray<DirectoryPersonSource | null> | null;
}

export interface DeployCompanyOption {
	/** Encoded `CompanyType` id — what an audience entry and a mutation both speak. */
	companyId: string;
	name: string;
	/** The raw id, kept for searching: an admin looks a company up by the number they know. */
	numericId: number;
}

export interface DeployPersonOption {
	personId: string;
	/** `Name (email)`, degrading to whichever the backend recorded. */
	label: string;
}

export interface DeployDirectory {
	companies: DeployCompanyOption[];
	personsByCompanyId: Map<string, DeployPersonOption[]>;
}

const isPresent = <T>(value: T | null | undefined): value is T => value != null;

export function encodeCompanyGlobalId(companyId: number | string): string {
	return encodeGlobalId('CompanyType', Number(companyId));
}

function personLabel(person: DirectoryPersonSource): string {
	const name = [person.firstName, person.lastName].filter(part => part).join(' ');
	const email = person.email ?? '';
	if (name && email) {
		return `${name} (${email})`;
	}
	return name || email;
}

/**
 * The company name with a trailing copy of its own id removed.
 *
 * MissionControl's directory already ends each name with the company id in brackets
 * ("ForecastJuanLocal (1)"), and everywhere this directory is displayed puts the id in front — so
 * without this the id appears twice: `1 - ForecastJuanLocal (1) (0 users)`.
 *
 * Only an exact match on this company's own id is removed, so a name that genuinely ends in brackets
 * ("Acme (Europe)", or a company literally numbered differently) is left alone. If stripping would
 * leave nothing at all, the original is kept — a blank row is worse than a repeated id.
 */
function companyName(rawName: string | null | undefined, numericId: number): string {
	const name = rawName ?? `Company ${numericId}`;
	const suffix = ` (${numericId})`;
	if (!name.endsWith(suffix)) {
		return name;
	}
	const stripped = name.slice(0, -suffix.length).trim();
	return stripped.length > 0 ? stripped : name;
}

export function buildDeployDirectory(source: DeployDirectorySource | null | undefined): DeployDirectory {
	const companies = (source?.companies ?? [])
		.filter(isPresent)
		.filter(company => company.companyId != null && !company.accessBlocked)
		.map(company => ({
			companyId: encodeCompanyGlobalId(company.companyId as number),
			name: companyName(company.name, company.companyId as number),
			numericId: company.companyId as number,
		}));

	const personsByCompanyId = new Map<string, DeployPersonOption[]>();
	const add = (companyGlobalId: string, person: DirectoryPersonSource) => {
		if (!person.id) {
			return;
		}
		const existing = personsByCompanyId.get(companyGlobalId) ?? [];
		existing.push({personId: person.id, label: personLabel(person)});
		personsByCompanyId.set(companyGlobalId, existing);
	};

	// Forecast's own people carry no company on their record, so they are filed under Forecast's
	// company explicitly — the same thing the feature-flags directory has to do.
	(source?.forecastPersons ?? [])
		.filter(isPresent)
		.forEach(person => add(encodeCompanyGlobalId(FORECAST_COMPANY_ID), person));

	(source?.otherPersons ?? []).filter(isPresent).forEach(person => {
		if (person.companyId) {
			add(person.companyId, person);
		}
	});

	return {companies, personsByCompanyId};
}

/**
 * `<numericId> - <name>`, the one builder every place a company is named draws from — the audience
 * table, the company picker and the audience modal — so a company reads identically wherever it
 * appears rather than three near-identical strings that quietly drift apart.
 */
export function companyLabel(company: Pick<DeployCompanyOption, 'numericId' | 'name'>): string {
	return `${company.numericId} - ${company.name}`;
}
